import { DialogButtons, useHistory, useNavigation, useSitemapItems, useTranslation, useUrlContext } from '@msdk/common'
import {
  ActionMenuBehaviour,
  BackgroundColor,
  Entity,
  EntityContext,
  FormMode,
  FormNode,
  ServerOperationResult,
} from '@msdk/definitions'
import { createComponentWrapperWithRef, FormWrapper, styled } from '@msdk/ui'
import React, { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useDialogModal } from '../../hooks'
import { useForm } from '../../hooks/form'
import { useEntryFromCollectionWithInternalId } from '../../state'
import { stringFormat, useTabs } from '../../utils'
import { saveValidated, ValidatedForm } from '../../utils/save-validation'
import { DynamicForm } from '../DynamicForm'
import { CustomLogicInvoker } from '../DynamicForm/custom-logic-invoker'
import { ActionsContainer } from '../FormActions/ActionsContainer'
import { FormHeader } from '../FormHeader/FormHeader'

const FormContainer = styled('div', { shouldForwardProp: (prop) => prop !== '$hasActions' })<{
  $hasActions?: boolean
}>`
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => (props.$hasActions ? '73px' : '0')};
`

/* StyledFormWrapper */
const MuiStyledFormWrapper = styled(FormWrapper, {
  shouldForwardProp: (prop) => prop !== '$mode' && prop !== '$centered' && prop !== '$darkBackground',
})<{
  $mode: string
  $centered: boolean | undefined
  $darkBackground: boolean | undefined
}>``
const NovaStyledFormWrapper = styled(FormWrapper, {
  shouldForwardProp: (prop) => prop !== '$mode' && prop !== '$centered' && prop !== '$darkBackground',
})<{
  $mode: string
  $centered: boolean | undefined
  $darkBackground: boolean | undefined
}>``
const StyledFormWrapper = createComponentWrapperWithRef(
  {
    mui: MuiStyledFormWrapper,
    nova: NovaStyledFormWrapper,
  },
  'StyledFormWrapper',
)

interface Props {
  entityName: string
  formName: string
  id: string
  mode: FormMode
}

export function FormPage(props: Props) {
  const validationRef = useRef<(() => Promise<ValidatedForm | null>) | null>(null)
  const t = useTranslation()
  const navigate = useNavigate()
  const frameworkNavigate = useNavigation()
  const dialog = useDialogModal()
  const history = useHistory()
  const { context } = useUrlContext()
  const sitemapItems = useSitemapItems()
  const { state } = useLocation()

  const {
    entityModel,
    formModel,
    form,
    entity,
    additionalEntities,
    entityLoaded,
    onSave,
    acl,
    invoker,
    onMoveToBin,
    onRestore,
    onDelete,
    onReloadEntity,
    rebindEntity,
    serverError,
    onClose: onCloseUseForm,
  } = useForm(props.entityName, props.formName, props.id, state?.PREFILLED_FIELDS)

  const { selectedTab, isFirstTab, isLastTab, updateSelectedTab, startingTab, moveToTab } = useTabs({
    form: form as FormNode,
  })
  useEffect(() => {
    const openErrorModal = async () => {
      const message = t('LBL_ENTITY_NOT_FOUND_MESSAGE', 'The specified {0} was not found')
      const title = t('LBL_ENTITY_NOT_FOUND_TITLE', '{0} not found')
      const formattedMessage = stringFormat(message, t(entityModel.idml, entityModel.description))
      const formattedTitle = stringFormat(title, t(entityModel.idml, entityModel.description))
      await dialog.createModal(formattedTitle, formattedMessage, DialogButtons.Ok)
      triggerNavigation()
    }

    if (serverError) {
      openErrorModal()
    }
  }, [serverError])

  const entityEntry = useEntryFromCollectionWithInternalId(entity?.internalId as string)

  /**
   * Look up the collection entry directly by the stable internalId (a client-side UUID that
   * useFormBase preserves through every setEntity call). This avoids the linear scan by server
   * entity.id that useEntityFromCollection performs, which can accidentally return a stale entry
   * (e.g. one left behind by a previous rebindEntity call) when multiple entries share the same
   * server entity.id.
   */
  const entityContext = entityEntry
    ? new EntityContext(entityEntry.entity, entityEntry.rootNode, entityEntry.additionalEntities, formModel)
    : null
  /**
   * this base entity context is used in the beginning as the entity collection
   * might not be yet populated at the first render of the form
   */
  const baseEntityContext = new EntityContext(entity as Entity, form as FormNode, additionalEntities, formModel)

  const triggerNavigation = () => {
    if (context['sId']) {
      // Of we have a sitemap item id instead of going blindly back from the history
      // we go back to the proper sitemap that opened this form
      const item = sitemapItems.find((it) => it.id === context['sId'])
      frameworkNavigate(`${item?.id}`, context.sitemap, context as Record<string, string>)
    } else {
      navigate(-1)
    }
  }

  const onClose = async () => {
    const { canLeaveForm } = await onCloseUseForm(dialog)
    if (canLeaveForm) {
      triggerNavigation()
    }
  }

  const onSaveAndNavigate = async (actionId?: string) => {
    const res = await saveValidated(validationRef.current, onSave, actionId)

    if (res.result !== ServerOperationResult.Success || res.event?.preventPostSaveNavigation) {
      return res
    }

    if (props.id === 'Create') {
      navigate(-1)
      return res
    }

    const navigateTo = state?.navigateTo ? (state?.navigateTo as string) : undefined

    if (navigateTo) {
      frameworkNavigate(
        navigateTo,
        context.sitemap,
        undefined,
        new URLSearchParams({
          mode: 'Readonly',
        }),
      )
      return res
    }

    const lastVisited = history.peek()

    if (!lastVisited?.params.id && lastVisited?.params.entityName === props.entityName) {
      navigate(-1)
    } else {
      const mainEntity = res.entities?.[0] ?? null

      const { entityName } = props
      const formName = entityModel.defaultForm ?? props.formName
      const _id = mainEntity?.id ?? props.id
      const _parentId = mainEntity?.parentId ?? props.id
      const id = entityModel.singleton ? _parentId : _id

      const url = [entityName, formName, id].join('/')

      frameworkNavigate(
        url,
        undefined,
        undefined,
        new URLSearchParams({
          mode: 'Readonly',
        }),
      )
    }
    return res
  }

  const hasActions = props.mode === 'Edit' && formModel.actionMenuBehaviour === ActionMenuBehaviour.Default

  return (
    <FormContainer $hasActions={hasActions}>
      {!formModel.hideFormHeader && (
        <FormHeader
          acl={acl}
          entity={entityContext ?? baseEntityContext}
          entityModel={entityModel}
          formModel={formModel}
          invoker={invoker as CustomLogicInvoker}
          onDelete={onDelete}
          onMoveToBin={onMoveToBin}
          onReloadEntity={onReloadEntity}
          onRestore={onRestore}
          onSave={onSaveAndNavigate}
          rootNode={form as FormNode}
          mode={props.mode}
          entityLoaded={entityLoaded}
          rebindEntitiy={rebindEntity}
        />
      )}
      <StyledFormWrapper
        $mode={props.mode}
        $darkBackground={formModel?.backgroundColor == BackgroundColor.Grey}
        $centered={formModel?.isCentered()}
      >
        <DynamicForm
          root={form as FormNode}
          entityLoaded={entityLoaded}
          entity={entity}
          additionalEntities={additionalEntities}
          entityModel={entityModel}
          formModel={formModel}
          acl={acl}
          invoker={invoker as CustomLogicInvoker}
          mode={props.mode}
          onDataChanged={() => onReloadEntity()}
          validationRef={validationRef}
          selectedTab={selectedTab}
          onChangeTab={updateSelectedTab}
          startingTab={startingTab}
          moveToTab={moveToTab}
        />
      </StyledFormWrapper>
      {hasActions && (
        <ActionsContainer
          acl={acl}
          entity={entityContext ?? baseEntityContext}
          entityModel={entityModel}
          formModel={formModel}
          invoker={invoker as CustomLogicInvoker}
          onDelete={onDelete}
          onMoveToBin={onMoveToBin}
          onReloadEntity={onReloadEntity}
          onRestore={onRestore}
          onSave={onSaveAndNavigate}
          onClose={onClose}
          rootNode={form as FormNode}
          entityLoaded={entityLoaded}
          rebindEntity={rebindEntity}
          selectedTab={selectedTab}
          isFirstTab={isFirstTab()}
          isLastTab={isLastTab()}
          moveToTab={moveToTab}
        />
      )}
    </FormContainer>
  )
}
